import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { UserLayout } from '../../components/layout/UserLayout';
import { userAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/helpers';
import { CheckCircle2, Copy, Send, UserPlus, UserMinus, Users, Wallet, Share2 } from 'lucide-react';

export const PublicProfilePage = () => {
  const { handle } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { load(); }, [handle]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await userAPI.getPublicProfile(handle);
      setProfile(data);
    } catch (error) {
      toast.error('User not found');
    } finally { setLoading(false); }
  };

  const toggleFollow = async () => {
    setBusy(true);
    try {
      if (profile.is_following) {
        await userAPI.unfollow(profile.id);
        toast.success('Unfollowed');
      } else {
        await userAPI.follow(profile.id);
        toast.success('Following!');
      }
      load();
    } catch (error) {
      toast.error('Action failed');
    } finally { setBusy(false); }
  };

  const addContact = async () => {
    try {
      await userAPI.addContact(profile.id);
      toast.success('Added to contacts');
    } catch (error) {
      toast.error('Failed to add');
    }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/@${profile.username}`;
    await copyToClipboard(url);
    toast.success('Profile link copied!');
  };

  const copyWallet = async () => {
    if (profile.public_wallet_address) {
      await copyToClipboard(profile.public_wallet_address);
      toast.success('Wallet address copied!');
    }
  };

  if (loading) {
    return <UserLayout><div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div></div></UserLayout>;
  }

  if (!profile) {
    return <UserLayout><Card><CardContent className="py-12 text-center">User not found</CardContent></Card></UserLayout>;
  }

  const isSelf = currentUser?.id === profile.id;

  return (
    <UserLayout>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="public-profile-page">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-semibold shrink-0">
                {(profile.name || profile.username || profile.email).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {profile.name || profile.username}
                    </h1>
                    {profile.verified && (
                      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 text-xs font-medium" data-testid="verified-badge">
                        <CheckCircle2 className="h-3 w-3" /> Verified
                      </div>
                    )}
                  </div>
                  {profile.username && <p className="text-muted-foreground">@{profile.username}</p>}
                </div>
                {profile.bio && <p className="text-sm">{profile.bio}</p>}
                <div className="flex items-center gap-4 text-sm">
                  <span><strong>{profile.followers_count}</strong> <span className="text-muted-foreground">followers</span></span>
                  <span><strong>{profile.following_count}</strong> <span className="text-muted-foreground">following</span></span>
                </div>
                {!isSelf && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={toggleFollow} disabled={busy} data-testid="follow-button" variant={profile.is_following ? 'outline' : 'default'}>
                      {profile.is_following ? <><UserMinus className="h-3 w-3 mr-1" /> Unfollow</> : <><UserPlus className="h-3 w-3 mr-1" /> Follow</>}
                    </Button>
                    <Link to={`/user/transfer?recipient=${profile.email}`}>
                      <Button size="sm" variant="outline" data-testid="send-button">
                        <Send className="h-3 w-3 mr-1" /> Send Funds
                      </Button>
                    </Link>
                    <Button size="sm" variant="outline" onClick={addContact} data-testid="add-contact-button">
                      <UserPlus className="h-3 w-3 mr-1" /> Save Contact
                    </Button>
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="ghost" onClick={copyLink} data-testid="copy-profile-link">
                    <Share2 className="h-3 w-3 mr-1" /> Copy Profile Link
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {profile.public_wallet_address && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Wallet className="h-4 w-4" />
                <p className="font-medium">Public Wallet Address</p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-2 p-3 rounded-lg bg-muted">
                <span className="font-mono text-sm truncate">{profile.public_wallet_address}</span>
                <Button size="sm" variant="ghost" onClick={copyWallet}><Copy className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </UserLayout>
  );
};
